/**
 * DQX CSV export utilities.
 *
 * Generates downloadable CSV files for:
 *  - Remapped file: user's data with mapped target column names as header row
 *  - Error report: per-cell validation failures
 *
 * In production, these will be generated server-side. The client-side
 * implementation here serves mock mode and as a fallback.
 */

import type { DqxContractRulesResponse } from "@/services/api";
import type { DqxColumnMapping, DqxErrorReportRow, ParsedWorksheet } from "@/types";

// ── CSV Helpers ───────────────────────────────────────────────

/** Escape a value for CSV: wrap in quotes if it contains comma, quote, or newline */
function csvEscape(value: string): string {
  if (value.includes('"') || value.includes(",") || value.includes("\n") || value.includes("\r")) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

function csvRow(values: string[]): string {
  return values.map(csvEscape).join(",");
}

// ── Remapped File CSV ─────────────────────────────────────────

/**
 * Generate a CSV of the user's uploaded data with the mapped target column
 * names inserted as the first row.
 *
 * Row 0: Mapped target column names (or original name if unmapped)
 * Row 1+: Data rows
 */
export function generateRemappedCsv(
  worksheet: ParsedWorksheet,
  columnMappings: DqxColumnMapping[]
): string {
  const renameMap = new Map<string, string>();
  for (const m of columnMappings) {
    renameMap.set(m.sourceColumn, m.targetColumn);
  }

  const sourceHeaders = worksheet.headers;
  const targetHeaders = sourceHeaders.map((h) => renameMap.get(h) ?? h);

  const lines: string[] = [];

  // Row 0: target column names
  lines.push(csvRow(targetHeaders));

  // Data rows
  for (const row of worksheet.rows) {
    const values = sourceHeaders.map((h) => String(row[h] ?? ""));
    lines.push(csvRow(values));
  }

  return lines.join("\n");
}

/**
 * Generate a CSV from an already-mapped grid.
 *
 * This is used when the UI has already resolved target-column ordering
 * (for example by schema field order) and we want downloads to match
 * that exact presentation.
 */
export function generateMappedGridCsv(headers: string[], rows: Record<string, unknown>[]): string {
  const lines: string[] = [];
  lines.push(csvRow(headers));

  for (const row of rows) {
    const values = headers.map((h) => String(row[h] ?? ""));
    lines.push(csvRow(values));
  }

  return lines.join("\n");
}

// ── Error Report CSV ──────────────────────────────────────────

/**
 * Generate a CSV error report with columns:
 * Column (Name), Column (Index), Row (Index), Value, Error, RuleType, Detail
 */
export function generateErrorReportCsv(errors: DqxErrorReportRow[]): string {
  const lines: string[] = [];

  lines.push(
    csvRow([
      "Column (Name)",
      "Column (Index)",
      "Row (Index)",
      "Value",
      "Error",
      "RuleType",
      "Detail",
    ])
  );

  for (const err of errors) {
    lines.push(
      csvRow([
        err.columnName,
        String(err.columnIndex + 1), // 1-indexed for humans
        String(err.rowIndex),
        err.value,
        err.error,
        err.ruleType ?? "",
        err.detail ?? "",
      ])
    );
  }

  return lines.join("\n");
}

// ── Data contract rules CSV ───────────────────────────────────

/**
 * CSV export of explicit DQX rules from GET /dqx/contract-rules.
 * Includes two metadata rows (contract name/path), then one row per rule.
 * Nested `check` is serialized as JSON in the last column.
 */
export function generateDataContractRulesCsv(data: DqxContractRulesResponse): string {
  const lines: string[] = [];
  lines.push(csvRow(["Contract name", data.contract_name]));
  lines.push(csvRow(["Contract path", data.contract_path]));
  lines.push(
    csvRow([
      "Rule name",
      "Scope",
      "Field",
      "Criticality",
      "Rule type",
      "Check function",
      "Check (JSON)",
    ])
  );

  for (const raw of data.contract_rules) {
    const r = raw as Record<string, unknown>;
    const check = r.check;
    let checkJson = "";
    if (check != null && typeof check === "object") {
      checkJson = JSON.stringify(check);
    } else if (check != null) {
      checkJson = String(check);
    }

    lines.push(
      csvRow([
        r.name != null ? String(r.name) : "",
        r.scope != null ? String(r.scope) : "",
        r.field != null ? String(r.field) : "",
        r.criticality != null ? String(r.criticality) : "",
        r.rule_type != null ? String(r.rule_type) : "",
        r.check_function != null ? String(r.check_function) : "",
        checkJson,
      ])
    );
  }

  return lines.join("\n");
}

// ── Download trigger ──────────────────────────────────────────

/** Trigger a browser download of a CSV string */
export function downloadCsv(content: string, filename: string): void {
  const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
  triggerBlobDownload(blob, filename);
}

/** Trigger a browser download of JSON (or other text) */
export function downloadJsonFile(content: string, filename: string): void {
  const blob = new Blob([content], { type: "application/json;charset=utf-8;" });
  triggerBlobDownload(blob, filename);
}

function triggerBlobDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.style.display = "none";
  document.body.appendChild(link);
  link.click();

  setTimeout(() => {
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, 100);
}
